/**
 * Extract embedded analytics visualizations from an assistant answer.
 *
 * The chatbot backend appends data visualizations as fenced ```chartspec blocks,
 * each carrying BOTH a Highcharts config and the equivalent markdown table:
 *
 * ```chartspec
 * { "title": "Top Groups", "chart": { ...Highcharts.Options... }, "table": "| # | Name | ... |" }
 * ```
 *
 * The two surfaces consume this differently:
 *  - the chat bubble renders the chart only (`stripChartSpecsForDisplay`)
 *  - copy/paste substitutes the table only (`replaceChartSpecsWithTables`)
 */

import type { Options } from 'highcharts'

const FENCE_RE = /```chartspec[^\n]*\n([\s\S]*?)```/g
// A trailing, not-yet-closed block while a response is still streaming.
const UNTERMINATED_RE = /```chartspec[^\n]*\n[\s\S]*$/

export interface ChartSpec {
  title?: string
  chart: Options
  table?: string
}

function parseSpecs(content: string): ChartSpec[] {
  const specs: ChartSpec[] = []
  FENCE_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = FENCE_RE.exec(content)) !== null) {
    const raw = (match[1] || '').trim()
    if (!raw) continue
    try {
      const parsed = JSON.parse(raw) as ChartSpec
      if (parsed && parsed.chart) specs.push(parsed)
    } catch {
      /* ignore malformed/partial spec */
    }
  }
  return specs
}

/** Charts to render in the chat bubble. */
export function extractCharts(content: string): Options[] {
  if (!content || !content.includes('```chartspec')) return []
  return parseSpecs(content).map((s) => s.chart)
}

/** Answer text for the bubble: chart fences removed (no inline tables). */
export function stripChartSpecsForDisplay(content: string): string {
  if (!content || !content.includes('```chartspec')) return content || ''
  return content
    .replace(FENCE_RE, '')
    .replace(UNTERMINATED_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd()
}

/** Answer text for copy/paste: each chart fence replaced by its markdown table. */
export function replaceChartSpecsWithTables(content: string): string {
  if (!content || !content.includes('```chartspec')) return content || ''
  FENCE_RE.lastIndex = 0
  return content
    .replace(FENCE_RE, (_full, body: string) => {
      try {
        const spec = JSON.parse((body || '').trim()) as ChartSpec
        return spec.table ? `\n${spec.table}\n` : ''
      } catch {
        return ''
      }
    })
    .replace(UNTERMINATED_RE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd()
}
